import React from 'react';
import {
  Box,
  Box as MuiBox,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { SwapHoriz, SwapVert, FileOpen, Menu as MenuIcon } from '@mui/icons-material';
import ActivityBar from './ActivityBar.jsx';
import CommandBar from './CommandBar.jsx';
import CommandPalette from './CommandPalette.jsx';
import BottomPanel from './BottomPanel.jsx';
import EditorTabs from './EditorTabs.jsx';
import NotificationsDrawer from './NotificationsDrawer.jsx';
import PaneRouter from './PaneRouter.jsx';
import MobileBottomNavigation from '../components/MobileBottomNavigation';
import { getWorkbenchNavItems } from './workbenchNavItems';
import { useEnterpriseAuth } from '../context/EnterpriseAuthContext';

export default function WorkbenchLayout({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const [notifItems] = React.useState([
    { title: 'New patient assigned', time: '5 min ago', read: false },
    { title: 'Critical lab flag: Hgb 7.2', time: '12 min ago', read: false },
    { title: 'Surgery schedule updated', time: '1h ago', read: true },
  ]);
  const [split, setSplit] = React.useState(() => {
    try {
      return localStorage.getItem('wb_split') || 'none';
    } catch {
      return 'none';
    }
  }); // none | vertical | horizontal
  const [bottomOpen, setBottomOpen] = React.useState(() => {
    try {
      const v = localStorage.getItem('wb_bottom_open');
      if (v === null || v === undefined) return false; // default closed to avoid content cut-off
      return v === '1';
    } catch {
      return false;
    }
  });
  const [ratioV, setRatioV] = React.useState(() => {
    try {
      return parseFloat(localStorage.getItem('wb_split_ratio_v') || '0.5');
    } catch {
      return 0.5;
    }
  });
  const [ratioH, setRatioH] = React.useState(() => {
    try {
      return parseFloat(localStorage.getItem('wb_split_ratio_h') || '0.5');
    } catch {
      return 0.5;
    }
  });
  const [secondaryPath, setSecondaryPath] = React.useState(() => {
    try {
      return localStorage.getItem('wb_secondary_path') || '/dashboard';
    } catch {
      return '/dashboard';
    }
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { user, federationMode } = useEnterpriseAuth();

  // Determine ASC mode using same priority as App.jsx:
  // 1. Federation mode from JWT (highest priority)
  // 2. User's mode property
  // 3. localStorage appMode
  // 4. URL path starting with /asc
  const isASC = React.useMemo(() => {
    try {
      // Federation mode takes highest priority
      if (federationMode === 'asc') return true;
      if (federationMode === 'hospital') return false;

      // Check user mode
      if (user?.mode === 'asc') return true;

      // Check localStorage
      if (localStorage.getItem('appMode') === 'asc') return true;

      // Check URL path
      return location.pathname.startsWith('/asc');
    } catch {
      return location.pathname.startsWith('/asc');
    }
  }, [location.pathname, federationMode, user?.mode]);
  const isEmbed = React.useMemo(() => {
    try {
      return (
        new URLSearchParams((globalThis.location && globalThis.location.search) || '').get(
          'embed'
        ) === '1'
      );
    } catch {
      return false;
    }
  }, []);

  React.useEffect(() => {
    if (isMobile && split !== 'none') {
      setSplit('none');
    }
  }, [isMobile, split]);

  React.useEffect(() => {
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, []);
  React.useEffect(() => {
    const moveHandler = e => {
      const path = e?.detail?.path;
      if (typeof path === 'string' && path.length > 0) {
        setSplit(prev => (prev === 'none' ? 'vertical' : prev));
        setSecondaryPath(path);
      }
    };
    const swapHandler = () => {
      const mainPath = (globalThis.location && globalThis.location.pathname) || '/';
      const sec = secondaryPath;
      if (sec && sec !== mainPath) {
        setSecondaryPath(mainPath);
        navigate(sec);
      }
    };
    globalThis.addEventListener('wb:move-current-to-secondary', moveHandler);
    globalThis.addEventListener('wb:swap-panes', swapHandler);
    return () => {
      globalThis.removeEventListener('wb:move-current-to-secondary', moveHandler);
      globalThis.removeEventListener('wb:swap-panes', swapHandler);
    };
  }, [secondaryPath, navigate]);

  const runAction = action => {
    if (!action) return;
    if (action.type === 'nav' && action.to) navigate(action.to);
  };

  const navItems = React.useMemo(() => getWorkbenchNavItems(isASC), [isASC]);
  const bottomNavItems = React.useMemo(() => {
    const pick = new Map(navItems.map(item => [item.key, item]));
    const baseKeys = ['worklist', 'readiness', 'orders', 'protocols'];
    const items = baseKeys
      .map(key => pick.get(key))
      .filter(Boolean)
      .map(item => ({
        label: item.label,
        value: item.key,
        icon: item.icon,
        path: item.path,
        match: (path) => path === item.path || path.startsWith(`${item.path}/`),
      }));
    items.push({
      label: 'Menu',
      value: 'menu',
      icon: <MenuIcon fontSize="small" />,
      action: () => setMobileNavOpen(true),
    });
    return items;
  }, [navItems]);

  React.useEffect(() => {
    try {
      localStorage.setItem('wb_split', split);
    } catch (e) {
      /* noop */
    }
  }, [split]);
  React.useEffect(() => {
    try {
      localStorage.setItem('wb_split_ratio_v', String(ratioV));
    } catch (e) {
      /* noop */
    }
  }, [ratioV]);
  React.useEffect(() => {
    try {
      localStorage.setItem('wb_split_ratio_h', String(ratioH));
    } catch (e) {
      /* noop */
    }
  }, [ratioH]);
  React.useEffect(() => {
    try {
      localStorage.setItem('wb_secondary_path', secondaryPath);
      localStorage.setItem('wb_secondary_last', secondaryPath);
    } catch (e) {
      /* noop */
    }
  }, [secondaryPath]);
  // Persist pairing of primary path to secondary path
  React.useEffect(() => {
    try {
      const key = `wb_pair:${(globalThis.location && globalThis.location.pathname) || '/'}`;
      localStorage.setItem(key, secondaryPath);
    } catch (e) {
      /* noop */
    }
  }, [secondaryPath]);
  // When entering split mode or changing primary route, load stored pairing once
  React.useEffect(() => {
    if (split === 'none') return;
    try {
      const key = `wb_pair:${location.pathname}`;
      const paired = localStorage.getItem(key);
      if (paired && paired !== secondaryPath) setSecondaryPath(paired);
    } catch (e) {
      /* noop */
    }
    // Only respond to split toggle and primary route changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [split, location.pathname]);

  React.useEffect(() => {
    const handler = e => {
      const path = e?.detail?.path;
      if (typeof path === 'string' && path.length > 0) {
        setSecondaryPath(path);
      }
    };
    globalThis.addEventListener('wb:open-in-pane', handler);
    return () => {
      try {
        globalThis.removeEventListener('wb:open-in-pane', handler);
      } catch (e) {
        /* noop */
      }
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        // Expose layout metrics as CSS variables for child views
        '--wb-cmdbar-h': '48px',
        '--wb-activitybar-w': '56px',
        '--wb-bottom-h': bottomOpen ? '220px' : '0px',
        '--wb-mobile-nav-h': isMobile ? '64px' : '0px',
      }}
    >
      {!isEmbed && !isMobile && <ActivityBar />}
      <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', minWidth: 0 }}>
        <CommandBar
          onOpenPalette={() => setPaletteOpen(true)}
          onNotificationsClick={() => setNotifOpen(true)}
        />
        {!isEmbed && !isMobile && <EditorTabs />}
        {!isEmbed && !isMobile && (
          <MuiBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 0.5,
              borderBottom: theme => `1px solid ${theme.palette.divider}`,
            }}
          >
            <ToggleButtonGroup
              size="small"
              value={split}
              exclusive
              onChange={(_, v) => {
                const next = v || 'none';
                if (next !== 'none' && split === 'none') {
                  try {
                    const main = (globalThis.location && globalThis.location.pathname) || '/';
                    let target = secondaryPath;
                    if (!target || target === main) {
                      const stored = localStorage.getItem('wb_secondary_last');
                      const candidates = [
                        stored,
                        '/patient-management',
                        '/surgeons',
                        '/protocols',
                        '/orders',
                        '/readiness',
                        '/settings',
                      ].filter(Boolean);
                      target = candidates.find(p => p && p !== main) || '/__choose__';
                      if (target !== secondaryPath) setSecondaryPath(target);
                    }
                  } catch (e) {
                    /* noop */
                  }
                }
                setSplit(next);
              }}
            >
              <ToggleButton value="none">Single</ToggleButton>
              <ToggleButton value="vertical">Split Vertical</ToggleButton>
              <ToggleButton value="horizontal">Split Horizontal</ToggleButton>
            </ToggleButtonGroup>
          </MuiBox>
        )}
        {split === 'none' && (
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              position: 'relative',
              // Let bottom panel overlap; keep anchors/scroll in view
              pb: isMobile ? 8 : 0,
              scrollPaddingBottom: 'calc(var(--wb-bottom-h) + var(--wb-mobile-nav-h))',
            }}
          >
            {children}
            {!isEmbed && (
              <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0 }}>
                <BottomPanel onOpenChange={setBottomOpen} />
              </Box>
            )}
          </Box>
        )}
        {split === 'vertical' && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `${Math.max(0.1, Math.min(0.9, ratioV)) * 100}% 4px auto`,
              flex: 1,
              minHeight: 0,
            }}
          >
            <Box sx={{ overflow: 'auto', position: 'relative', scrollPaddingBottom: 'var(--wb-bottom-h)' }}>{children}</Box>
            <Box
              sx={{
                position: 'relative',
                cursor: 'col-resize',
                bgcolor: theme => theme.palette.action.hover,
                '&:hover .wb-divider-controls': { opacity: 1 },
              }}
              onMouseDown={e => {
                const startX = e.clientX;
                const startRatio = ratioV;
                const onMove = ev => {
                  const delta = ev.clientX - startX;
                  const total = e.currentTarget.parentElement?.getBoundingClientRect().width || 1;
                  const newRatio = Math.max(0.1, Math.min(0.9, startRatio + delta / total));
                  setRatioV(newRatio);
                };
                const onUp = () => {
                  globalThis.removeEventListener('mousemove', onMove);
                  globalThis.removeEventListener('mouseup', onUp);
                };
                globalThis.addEventListener('mousemove', onMove);
                globalThis.addEventListener('mouseup', onUp);
              }}
            >
              <MuiBox
                className="wb-divider-controls"
                sx={{
                  opacity: 0,
                  transition: 'opacity 120ms',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  gap: 0.5,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: 1,
                  p: 0.25,
                }}
              >
                <Tooltip title="Swap panes">
                  <IconButton
                    size="small"
                    onClick={ev => {
                      ev.stopPropagation();
                      globalThis.dispatchEvent(new CustomEvent('wb:swap-panes'));
                    }}
                  >
                    <SwapHoriz fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Move current to secondary">
                  <IconButton
                    size="small"
                    onClick={ev => {
                      ev.stopPropagation();
                      globalThis.dispatchEvent(
                        new CustomEvent('wb:move-current-to-secondary', {
                          detail: {
                            path: (globalThis.location && globalThis.location.pathname) || '/',
                          },
                        })
                      );
                    }}
                  >
                    <FileOpen fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </MuiBox>
            </Box>
            <Box
              sx={{
                overflow: 'hidden',
                position: 'relative',
                borderLeft: theme => `1px solid ${theme.palette.divider}`,
              }}
            >
              <PaneRouter
                initialPath={secondaryPath}
                targetPath={secondaryPath}
                onPathChange={setSecondaryPath}
              />
            </Box>
          </Box>
        )}
        {split === 'horizontal' && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateRows: `${Math.max(0.1, Math.min(0.9, ratioH)) * 100}% 4px auto`,
              flex: 1,
              minHeight: 0,
            }}
          >
            <Box sx={{ overflow: 'auto', position: 'relative', scrollPaddingBottom: 'var(--wb-bottom-h)' }}>{children}</Box>
            <Box
              sx={{
                position: 'relative',
                cursor: 'row-resize',
                bgcolor: theme => theme.palette.action.hover,
                '&:hover .wb-divider-controls': { opacity: 1 },
              }}
              onMouseDown={e => {
                const startY = e.clientY;
                const startRatio = ratioH;
                const onMove = ev => {
                  const delta = ev.clientY - startY;
                  const total = e.currentTarget.parentElement?.getBoundingClientRect().height || 1;
                  const newRatio = Math.max(0.1, Math.min(0.9, startRatio + delta / total));
                  setRatioH(newRatio);
                };
                const onUp = () => {
                  globalThis.removeEventListener('mousemove', onMove);
                  globalThis.removeEventListener('mouseup', onUp);
                };
                globalThis.addEventListener('mousemove', onMove);
                globalThis.addEventListener('mouseup', onUp);
              }}
            >
              <MuiBox
                className="wb-divider-controls"
                sx={{
                  opacity: 0,
                  transition: 'opacity 120ms',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  gap: 0.5,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: 1,
                  p: 0.25,
                }}
              >
                <Tooltip title="Swap panes">
                  <IconButton
                    size="small"
                    onClick={ev => {
                      ev.stopPropagation();
                      globalThis.dispatchEvent(new CustomEvent('wb:swap-panes'));
                    }}
                  >
                    <SwapVert fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Move current to secondary">
                  <IconButton
                    size="small"
                    onClick={ev => {
                      ev.stopPropagation();
                      globalThis.dispatchEvent(
                        new CustomEvent('wb:move-current-to-secondary', {
                          detail: {
                            path: (globalThis.location && globalThis.location.pathname) || '/',
                          },
                        })
                      );
                    }}
                  >
                    <FileOpen fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </MuiBox>
            </Box>
            <Box
              sx={{
                overflow: 'hidden',
                position: 'relative',
                borderTop: theme => `1px solid ${theme.palette.divider}`,
              }}
            >
              <PaneRouter
                initialPath={secondaryPath}
                targetPath={secondaryPath}
                onPathChange={setSecondaryPath}
              />
            </Box>
          </Box>
        )}
      </Box>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} onRun={runAction} />
      {!isEmbed && (
        <NotificationsDrawer
          open={notifOpen}
          onClose={() => setNotifOpen(false)}
          items={notifItems}
        />
      )}
      {!isEmbed && isMobile && (
        <>
          <Drawer
            anchor="left"
            open={mobileNavOpen}
            onClose={() => setMobileNavOpen(false)}
            PaperProps={{ sx: { width: 280 } }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                Navigation
              </Typography>
            </Box>
            <Divider />
            <List dense sx={{ py: 0.5 }}>
              {navItems.map(item => {
                const active =
                  location.pathname === item.path ||
                  location.pathname.startsWith(`${item.path}/`);
                return (
                  <ListItemButton
                    key={item.key}
                    selected={active}
                    onClick={() => {
                      navigate(item.path);
                      setMobileNavOpen(false);
                    }}
                    sx={{ mx: 1, borderRadius: 1.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                );
              })}
            </List>
          </Drawer>
          <MobileBottomNavigation items={bottomNavItems} />
        </>
      )}
    </Box>
  );
}
